import "@/Fresh.css";
import { useEffect, useState } from "react";
import axios from "axios";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  PhoneCall,
  Send,
  ShieldCheck,
  Wrench,
  X,
} from "lucide-react";
import { toast, Toaster } from "./components/ui/sonner";
import {
  applianceServices,
  brands,
  contactInfo,
  heroStats,
  media,
  navItems,
  processSteps,
  productServices,
  serviceAreas,
  serviceCategories,
  serviceOptions,
  services,
  trustPoints,
} from "./data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const SectionIntro = ({ eyebrow, title, description, testId }) => (
  <div className="section-intro" data-testid={`${testId}-intro`}>
    <div>
      <span data-testid={`${testId}-eyebrow`}>{eyebrow}</span>
      <h2 data-testid={`${testId}-heading`}>{title}</h2>
    </div>
    <p data-testid={`${testId}-description`}>{description}</p>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const nav = (prefix = "nav") =>
    navItems.map((item) => (
      <a
        key={`${prefix}-${item.id}`}
        href={`#${item.id}`}
        className="nav-link"
        data-testid={`${prefix}-${item.id}-link`}
        onClick={() => setOpen(false)}
      >
        {item.label}
      </a>
    ));

  return (
    <header className="site-header" data-testid="site-header">
      <div className="top-bar" data-testid="top-service-announcement">
        <span data-testid="top-bar-offer">AC service starts at ₹599/-</span>
        <a href={contactInfo.primaryPhoneHref} data-testid="announcement-call-link">
          Call {contactInfo.primaryPhone}
        </a>
      </div>
      <div className="nav-shell">
        <a href="#home" className="brand" data-testid="brand-home-link">
          <img src={media.logo} alt="RK Air Conditioners" data-testid="brand-logo-image" />
          <span>
            <strong data-testid="brand-name">{contactInfo.businessName}</strong>
            <small data-testid="brand-owner">{contactInfo.owner}</small>
          </span>
        </a>
        <nav className="desktop-nav" data-testid="desktop-navigation" aria-label="Main navigation">
          {nav()}
        </nav>
        <div className="nav-actions" data-testid="header-actions">
          <a className="btn btn-blue" href={contactInfo.primaryPhoneHref} data-testid="header-call-button">
            <PhoneCall aria-hidden="true" /> Call Now
          </a>
          <a className="btn btn-light" href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="header-whatsapp-button">
            <MessageCircle aria-hidden="true" /> WhatsApp
          </a>
        </div>
        <button className="menu-button" type="button" data-testid="mobile-menu-toggle-button" onClick={() => setOpen(!open)} aria-label="Open menu">
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div className="mobile-menu" data-testid="mobile-navigation-panel">
          {nav("mobile-nav")}
          <a className="mobile-menu-action" href={contactInfo.primaryPhoneHref} data-testid="mobile-call-link">Call {contactInfo.primaryPhone}</a>
          <a className="mobile-menu-action dark" href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-link">WhatsApp Directly</a>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="home" className="hero" data-testid="home-section">
    <div className="hero-grid">
      <div className="hero-copy" data-testid="hero-copy">
        <div className="eyebrow-pill" data-testid="hero-service-badge">
          <ShieldCheck aria-hidden="true" /> Professional AC & Appliance Experts
        </div>
        <h1 data-testid="hero-heading">Beat the heat with expert AC service.</h1>
        <p data-testid="hero-subheading">Fast repair, installation, cleaning, sale-purchase and rental support for air conditioners and home appliances across West Delhi.</p>
        <div className="hero-actions" data-testid="hero-primary-actions">
          <a className="btn btn-blue" href={contactInfo.primaryPhoneHref} data-testid="hero-call-button"><PhoneCall aria-hidden="true" /> Call for Service</a>
          <a className="btn btn-light" href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button"><MessageCircle aria-hidden="true" /> WhatsApp Now</a>
        </div>
        <div className="trust-list" data-testid="hero-trust-list">
          {trustPoints.map((point) => <span key={point} data-testid={`hero-trust-${slug(point)}`}><BadgeCheck aria-hidden="true" /> {point}</span>)}
        </div>
      </div>
      <div className="hero-photo" data-testid="hero-visual-card">
        <img src={media.professionalAcCleaning} alt="Professional technician washing a split AC" data-testid="hero-professional-ac-cleaning-image" />
        <div className="price-card" data-testid="hero-price-card">
          <span>AC Service Starts</span>
          <strong>₹599/-</strong>
          <a href={contactInfo.primaryPhoneHref} data-testid="hero-price-card-call-link">Book now <ArrowRight aria-hidden="true" /></a>
        </div>
      </div>
    </div>
    <div className="stats" data-testid="hero-stats-strip">
      {heroStats.map((stat) => (
        <div className="stat" key={stat.label} data-testid={`hero-stat-${slug(stat.label)}`}>
          <strong>{stat.value}</strong><span>{stat.label}</span>
        </div>
      ))}
    </div>
  </section>
);

const Services = () => (
  <>
    <section id="services" className="section" data-testid="services-section">
      <SectionIntro testId="services" eyebrow="Service List" title="Complete AC and appliance care in one place." description="From urgent AC repair to full installation and regular cleaning, RK Air Conditioners keeps your appliances running smoothly." />
      <div className="category-grid" data-testid="service-category-strip">
        {serviceCategories.map((category) => (
          <article className="category-card" key={category.id} data-testid={`service-category-card-${category.id}`}>
            <span data-testid={`service-category-label-${category.id}`}>{category.label}</span>
            <h3 data-testid={`service-category-title-${category.id}`}>{category.title}</h3>
            <p data-testid={`service-category-description-${category.id}`}>{category.description}</p>
            <ul data-testid={`service-category-highlights-${category.id}`}>{category.highlights.map((item) => <li key={item} data-testid={`service-category-highlight-${category.id}-${slug(item)}`}><CheckCircle2 aria-hidden="true" /> {item}</li>)}</ul>
          </article>
        ))}
      </div>
      <div className="service-grid" data-testid="service-bento-grid">
        {services.map((service) => (
          <article className={`service-card ${service.featured ? "featured" : ""}`} key={service.id} data-testid={`service-card-${service.id}`}>
            <img src={service.image} alt={service.title} data-testid={`service-image-${service.id}`} />
            <div className="service-body">
              <span data-testid={`service-kicker-${service.id}`}>{service.kicker}</span>
              <h3 data-testid={`service-title-${service.id}`}>{service.title}</h3>
              <p data-testid={`service-description-${service.id}`}>{service.description}</p>
              <ul data-testid={`service-list-${service.id}`}>{service.items.map((item) => <li key={item} data-testid={`service-item-${service.id}-${slug(item)}`}><CheckCircle2 aria-hidden="true" /> {item}</li>)}</ul>
              <a href="#contact" className="btn btn-light wide" data-testid={`service-contact-button-${service.id}`}>Request service <ArrowRight aria-hidden="true" /></a>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section className="section appliance-section" data-testid="appliance-services-section">
      <SectionIntro testId="appliance-services" eyebrow="Other Appliances" title="Detailed service for fridge, washing machine, TV and microwave." description="Along with AC work, the team handles daily-use appliances with repair, maintenance, installation and PCB-level support." />
      <div className="appliance-grid" data-testid="appliance-services-grid">
        {applianceServices.map((appliance) => (
          <article className="appliance-card" key={appliance.id} data-testid={`appliance-service-card-${appliance.id}`}>
            <div className="appliance-code" data-testid={`appliance-service-index-${appliance.id}`}>{appliance.title.slice(0, 2).toUpperCase()}</div>
            <h3 data-testid={`appliance-service-title-${appliance.id}`}>{appliance.title}</h3>
            <p data-testid={`appliance-service-description-${appliance.id}`}>{appliance.description}</p>
            <div className="chips" data-testid={`appliance-service-problems-${appliance.id}`}>{appliance.problems.map((problem) => <span key={problem} data-testid={`appliance-service-problem-${appliance.id}-${slug(problem)}`}>{problem}</span>)}</div>
            <a href="#contact" className="btn btn-light wide" data-testid={`appliance-service-contact-button-${appliance.id}`}>Ask for service <ArrowRight aria-hidden="true" /></a>
          </article>
        ))}
      </div>
    </section>
  </>
);

const Products = () => (
  <section id="products" className="section products" data-testid="products-section">
    <div className="split-grid">
      <div className="product-photo" data-testid="products-visual-card">
        <img src={media.appliances} alt="Home appliances" data-testid="products-appliance-image" />
        <div data-testid="products-quality-badge">New & Used • Warranty Support</div>
      </div>
      <div>
        <span className="section-label" data-testid="products-eyebrow">Sale • Purchase • Rent</span>
        <h2 data-testid="products-heading">Quality appliances without confusion.</h2>
        <p data-testid="products-description">Buy, sell or rent appliances with reliable guidance, transparent communication and practical support from experienced technicians.</p>
        <div className="product-cards" data-testid="product-service-grid">
          {productServices.map((product) => (
            <article className="plain-card" key={product.id} data-testid={`product-service-card-${product.id}`}>
              <h3 data-testid={`product-service-title-${product.id}`}>{product.title}</h3>
              <p data-testid={`product-service-description-${product.id}`}>{product.description}</p>
              <div className="chips" data-testid={`product-service-list-${product.id}`}>{product.items.map((item) => <span key={item} data-testid={`product-service-item-${product.id}-${slug(item)}`}>{item}</span>)}</div>
            </article>
          ))}
        </div>
        <div className="inline-actions" data-testid="products-actions">
          <a className="btn btn-blue" href={contactInfo.primaryPhoneHref} data-testid="products-call-button">Call for availability</a>
          <a className="btn btn-light" href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="products-whatsapp-button">WhatsApp requirement</a>
        </div>
      </div>
    </div>
  </section>
);

const About = () => (
  <>
    <section id="about" className="section about" data-testid="about-section">
      <div className="split-grid about-grid">
        <div>
          <span className="section-label" data-testid="about-eyebrow">About Us</span>
          <h2 data-testid="about-heading">A trusted local service team with 15+ years of experience.</h2>
          <p data-testid="about-description">RK Air Conditioners is a leading provider of electrical appliances and services in the region. We deliver top-quality products and services at affordable prices, with a reputation built on excellence and reliability.</p>
          <div className="callout" data-testid="about-satisfaction-callout">Your satisfaction is our top priority.</div>
        </div>
        <div className="process-grid" data-testid="process-grid">
          {processSteps.map((step) => <article className="process-card" key={step.step} data-testid={`process-card-${step.step}`}><span data-testid={`process-step-${step.step}`}>{step.step}</span><h3 data-testid={`process-title-${step.step}`}>{step.title}</h3><p data-testid={`process-text-${step.step}`}>{step.text}</p></article>)}
        </div>
      </div>
      <div className="coverage" data-testid="coverage-row">
        <div data-testid="coverage-location-card"><MapPin aria-hidden="true" /> {serviceAreas.join(" • ")}</div>
        <div data-testid="coverage-support-card"><Wrench aria-hidden="true" /> Repair • Cleaning • Installation • Rental</div>
        <a href={contactInfo.primaryPhoneHref} data-testid="coverage-phone-link"><PhoneCall aria-hidden="true" /> {contactInfo.primaryPhone}</a>
      </div>
    </section>
    <section className="brands" data-testid="brands-section">
      <div className="brands-title" data-testid="brands-heading">Service support for leading appliance brands</div>
      <Marquee gradient={false} speed={35} pauseOnHover>
        {brands.map((logo, index) => <div className="brand-card" key={logo} data-testid={`brand-logo-card-${index + 1}`}><img src={logo} alt={`Supported brand ${index + 1}`} data-testid={`brand-logo-image-${index + 1}`} /></div>)}
      </Marquee>
    </section>
  </>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "AC Repair", message: "" });
  const [loading, setLoading] = useState(false);
  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, email: form.email || null });
      toast.success("Request submitted. RK Air Conditioners will contact you soon.");
      setForm({ name: "", phone: "", email: "", service: "AC Repair", message: "" });
    } catch (error) {
      toast.error("Could not submit right now. Please call or WhatsApp directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section contact" data-testid="contact-section">
      <div className="split-grid contact-grid">
        <div>
          <span className="section-label" data-testid="contact-eyebrow">Request your complaint</span>
          <h2 data-testid="contact-heading">Need AC service today? Contact RK Air Conditioners.</h2>
          <p data-testid="contact-description">Fill the form or use the direct call and WhatsApp buttons for quick AC repair, cleaning, installation, appliance sale-purchase or rental support.</p>
          <div className="contact-cards" data-testid="contact-info-cards">
            <a className="contact-card" href={contactInfo.primaryPhoneHref} data-testid="contact-primary-phone-link"><PhoneCall aria-hidden="true" /><span><small>Call Now</small><strong>{contactInfo.primaryPhone}</strong></span></a>
            <a className="contact-card" href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><MessageCircle aria-hidden="true" /><span><small>WhatsApp</small><strong>Direct booking</strong></span></a>
            <div className="contact-card" data-testid="contact-address-card"><MapPin aria-hidden="true" /><span><small>Address</small><strong>{contactInfo.address}</strong></span></div>
            <div className="contact-card" data-testid="contact-email-card"><Mail aria-hidden="true" /><span><small>Email</small><strong>{contactInfo.emails[0]}</strong></span></div>
          </div>
        </div>
        <form className="inquiry-form" onSubmit={submit} data-testid="inquiry-form">
          <label data-testid="name-field-label">Name<input data-testid="inquiry-name-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Your full name" required minLength={2} /></label>
          <div className="form-two">
            <label data-testid="phone-field-label">Phone<input data-testid="inquiry-phone-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="Mobile number" required minLength={7} /></label>
            <label data-testid="email-field-label">Email<input data-testid="inquiry-email-input" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="Optional email" /></label>
          </div>
          <label data-testid="service-field-label">Service Required<select data-testid="inquiry-service-select" value={form.service} onChange={(e) => setField("service", e.target.value)} required>{serviceOptions.map((option) => <option key={option} value={option} data-testid={`inquiry-service-option-${slug(option)}`}>{option}</option>)}</select></label>
          <label data-testid="message-field-label">Message<textarea data-testid="inquiry-message-textarea" value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Tell us your appliance issue, area and preferred time." required minLength={5} rows={5} /></label>
          <button className="btn btn-blue submit" type="submit" disabled={loading} data-testid="inquiry-submit-button">{loading ? "Submitting..." : "Submit Request"} <Send aria-hidden="true" /></button>
          <p data-testid="inquiry-form-note">For urgent service, call or WhatsApp directly.</p>
        </form>
      </div>
      <div className="mobile-sticky" data-testid="mobile-sticky-cta"><a href={contactInfo.primaryPhoneHref} data-testid="mobile-sticky-call-link"><PhoneCall aria-hidden="true" /> Call</a><a href={contactInfo.whatsappHref} target="_blank" rel="noreferrer" data-testid="mobile-sticky-whatsapp-link"><MessageCircle aria-hidden="true" /> WhatsApp</a></div>
    </section>
  );
};

const Footer = () => (
  <footer className="footer" data-testid="site-footer">
    <div className="footer-grid">
      <div><span data-testid="footer-kicker">RK Air Conditioners</span><h2 data-testid="footer-heading">Reliable cooling. Cleaner appliances. Faster service.</h2></div>
      <div className="footer-list" data-testid="footer-contact-details"><p data-testid="footer-owner">{contactInfo.owner}</p><a href={contactInfo.primaryPhoneHref} data-testid="footer-phone-link">{contactInfo.primaryPhone}</a><a href={contactInfo.secondaryPhoneHref} data-testid="footer-secondary-phone-link">{contactInfo.secondaryPhone}</a><p data-testid="footer-address">{contactInfo.address}</p></div>
      <nav className="footer-list" data-testid="footer-navigation">{navItems.map((item) => <a key={item.id} href={`#${item.id}`} data-testid={`footer-${item.id}-link`}>{item.label}</a>)}</nav>
      <div className="footer-list" data-testid="footer-social-links"><a href={contactInfo.social.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube-link">YouTube</a><a href={contactInfo.social.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook-link">Facebook</a><a href={contactInfo.social.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram-link">Instagram</a></div>
    </div>
  </footer>
);

function App() {
  useEffect(() => {
    const tagExternalBadge = () => {
      document.querySelectorAll("a").forEach((link) => {
        if (link.textContent?.includes("Made with Emergent")) {
          link.setAttribute("data-testid", "external-made-with-emergent-link");
        }
      });
    };

    tagExternalBadge();
    const observer = new MutationObserver(tagExternalBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <main data-testid="rk-airconditioners-website">
        <Header />
        <Hero />
        <Services />
        <Products />
        <About />
        <Contact />
        <Footer />
      </main>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
